// Where a quiz sits relative to its open/close window.
//
// The server computes this too and returns it as `state`. This mirror exists so
// the console can render a sensible label for a quiz that predates the field,
// and so the two definitions can be checked against each other.
//
// The stored `status` field on a quiz is written once at creation and never
// advances, so it must not be used to decide whether a test is live.

export const QUIZ_STATE = {
  UPCOMING: 'upcoming',
  OPEN: 'open',
  CLOSED: 'closed',
};

const LABELS = {
  [QUIZ_STATE.UPCOMING]: 'Upcoming',
  [QUIZ_STATE.OPEN]: 'Open',
  [QUIZ_STATE.CLOSED]: 'Closed',
};

function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Compute a quiz's state.
 *
 * A missing `opensAt` means it opened immediately; a missing `closesAt` means it
 * never closes. At the exact boundary a quiz counts as open at `opensAt` and as
 * closed at `closesAt`, matching the server.
 */
export function computeQuizState(quiz, now = new Date()) {
  const opensAt = toDate(quiz?.opensAt);
  const closesAt = toDate(quiz?.closesAt);

  if (opensAt && now < opensAt) return QUIZ_STATE.UPCOMING;
  if (closesAt && now >= closesAt) return QUIZ_STATE.CLOSED;
  return QUIZ_STATE.OPEN;
}

/** The state to show: the server's if it sent one, otherwise computed here. */
export function resolveQuizState(quiz, now = new Date()) {
  const serverState = quiz?.state;
  if (serverState && LABELS[serverState]) return serverState;
  return computeQuizState(quiz, now);
}

export function quizStateLabel(state) {
  return LABELS[state] || 'Unknown';
}
